import {
    AutomationRule,
    AutomationWorker,
    AutomationWorkerId,
    cloneAutomationWorkers,
} from './worker-automation.types';
import { AUTOMATION_WORKER_DEFINITIONS } from './worker-automation.constants';
import {
    normalizeAutomationRuleForEdit,
    normalizeAutomationRuleForPersist,
} from './worker-automation.utils';
import { WorkflowConfigMap, WorkflowStatusConfig } from '../worker-configuration.types';

const WORKER_KEY_TO_ID = Object.fromEntries(
    Object.entries(AUTOMATION_WORKER_DEFINITIONS).map(([id, definition]) => [
        definition.key,
        id as AutomationWorkerId,
    ]),
) as Record<string, AutomationWorkerId>;

export function createEmptyAutomationWorkers(): Record<AutomationWorkerId, AutomationWorker> {
    return cloneAutomationWorkers(
        Object.fromEntries(
            Object.entries(AUTOMATION_WORKER_DEFINITIONS).map(([id, definition]) => [
                id,
                {
                    ...definition,
                    rules: [],
                },
            ]),
        ) as Record<AutomationWorkerId, AutomationWorker>,
    );
}

function isAutomationWorkerConfigKey(key: string): key is string {
    return key in WORKER_KEY_TO_ID;
}

function sortRulesByPriority(rules: AutomationRule[]): AutomationRule[] {
    if (!rules.some(rule => typeof rule.priority === 'number')) {
        return rules;
    }

    return [...rules].sort((left, right) => (left.priority ?? 0) - (right.priority ?? 0));
}

function parseAutomationWorkerValue(
    workerId: AutomationWorkerId,
    value: WorkflowStatusConfig,
): AutomationWorker {
    const base = createEmptyAutomationWorkers()[workerId];
    const payload = value as {
        active?: boolean;
        rules?: AutomationRule[];
    };

    return {
        ...base,
        active: typeof payload.active === 'boolean' ? payload.active : base.active,
        rules: Array.isArray(payload.rules)
            ? sortRulesByPriority(
                  payload.rules.map((rule: AutomationRule) => normalizeAutomationRuleForEdit(rule)),
              )
            : [],
    };
}

export function splitWorkflowConfiguration(config?: WorkflowConfigMap): {
    automationWorkers: Record<AutomationWorkerId, AutomationWorker>;
    workflowConfig: WorkflowConfigMap;
} {
    const workflowConfig: WorkflowConfigMap = {};
    const automationWorkers = createEmptyAutomationWorkers();

    if (!config) {
        return { workflowConfig, automationWorkers };
    }

    for (const [key, value] of Object.entries(config)) {
        if (isAutomationWorkerConfigKey(key)) {
            const workerId = WORKER_KEY_TO_ID[key];
            automationWorkers[workerId] = parseAutomationWorkerValue(workerId, value);
            continue;
        }

        workflowConfig[key] = value;
    }

    return { workflowConfig, automationWorkers };
}

export function mergeWorkflowConfiguration(
    workflowConfig: WorkflowConfigMap,
    automationWorkers: Record<AutomationWorkerId, AutomationWorker>,
): WorkflowConfigMap {
    const merged: WorkflowConfigMap = JSON.parse(JSON.stringify(workflowConfig ?? {}));

    for (const worker of Object.values(automationWorkers)) {
        merged[worker.key] = {
            active: worker.active,
            rules: worker.rules.map((rule, index) => ({
                ...normalizeAutomationRuleForPersist(rule),
                priority: index + 1,
            })),
        } as unknown as WorkflowStatusConfig;
    }

    return merged;
}
